// src/pages/Admin/Withdrawals/AdminWithdrawalDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaTimes, FaUser, FaUniversity, FaCalendar, FaInfoCircle } from 'react-icons/fa';
import Sidebar from '../../../components/Common/Sidebar';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const AdminWithdrawalDetails = () => {
  const { withdrawalId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [withdrawal, setWithdrawal] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWithdrawalDetails();
  }, [withdrawalId]);

  const fetchWithdrawalDetails = async () => {
    try {
      setLoading(true);
      // Fetch from withdrawals list and find specific one
      const response = await adminAPI.withdrawals.getAll({ 
        search: '',
        per_page: 100 
      });
      
      const foundWithdrawal = response.data.data.withdrawals.find(
        w => w.id === parseInt(withdrawalId)
      );
      
      if (foundWithdrawal) {
        setWithdrawal(foundWithdrawal);
      }
    } catch (error) {
      toast.error('Failed to fetch withdrawal details');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWithdrawal = async () => {
    try {
      setProcessing(true);
      await adminAPI.withdrawals.approve(withdrawalId);
      toast.success('Withdrawal approved and processed successfully');
      setShowApproveModal(false);
      navigate('/admin/withdrawals');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectWithdrawal = async () => {
    if (!rejectionRemarks.trim() || rejectionRemarks.length < 10) {
      toast.error('Please provide rejection remarks (minimum 10 characters)');
      return;
    }

    try {
      setProcessing(true);
      await adminAPI.withdrawals.reject(withdrawalId, { remarks: rejectionRemarks });
      toast.success('Withdrawal rejected successfully');
      setShowRejectModal(false);
      navigate('/admin/withdrawals');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      approved: 'info',
      processed: 'success',
      rejected: 'danger',
      cancelled: 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'processed':
        return '✓';
      case 'pending':
        return '⏱';
      case 'rejected':
      case 'cancelled':
        return '✗';
      case 'approved':
        return '👍';
      default:
        return '•';
    }
  };

  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--accent-cyan)' }} />
            <p className="mt-3">Loading withdrawal details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!withdrawal) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <h5>Withdrawal request not found</h5>
            <Button variant="primary" onClick={() => navigate('/admin/withdrawals')}>
              Back to Withdrawals
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
<div className="dashboard-layout">
  <Sidebar role="admin" />
  <div className="dashboard-content">

        <div className="mb-4">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/admin/withdrawals')}
            className="mb-3"
          >
            <FaArrowLeft className="me-2" />
            Back to Withdrawals
          </Button>
          
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="fw-bold mb-1">Withdrawal Request Details</h2>
              <p className="text-muted mb-0 text-monospace">{withdrawal.request_id}</p>
            </div>
            
            {withdrawal.status === 'pending' && (
              <div className="d-flex gap-2">
                <Button 
                  variant="success" 
                  onClick={() => setShowApproveModal(true)}
                >
                  <FaCheck className="me-2" />
                  Approve & Process
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => setShowRejectModal(true)}
                >
                  <FaTimes className="me-2" />
                  Reject Request
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Status Alert */}
        {withdrawal.status === 'pending' && (
          <Alert variant="warning" className="mb-4">
            <strong>⚠️ Pending Approval:</strong> This withdrawal request is awaiting your review. 
            Please verify the bank details and approve or reject accordingly.
          </Alert>
        )}

        {withdrawal.status === 'rejected' && withdrawal.admin_remarks && (
          <Alert variant="danger" className="mb-4">
            <strong>✗ Rejected:</strong> {withdrawal.admin_remarks}
          </Alert>
        )}

        {withdrawal.status === 'processed' && (
          <Alert variant="success" className="mb-4">
            <strong>✓ Processed:</strong> This withdrawal has been successfully processed on{' '}
            {new Date(withdrawal.processed_at).toLocaleString()}
          </Alert>
        )}

        <div className="row g-4">
          {/* Withdrawal Information */}
          <div className="col-md-8">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaInfoCircle className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Withdrawal Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: '40%' }}>Request ID:</td>
                      <td className="fw-bold text-monospace">{withdrawal.request_id}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Status:</td>
                      <td>
                        <Badge bg={getStatusBadge(withdrawal.status)} className="px-3 py-2">
                          {getStatusIcon(withdrawal.status)} {withdrawal.status.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Amount:</td>
                      <td>
                        <h4 className="mb-0 fw-bold" style={{ color: 'var(--accent-cyan)' }}>
                          ₹{parseFloat(withdrawal.amount).toFixed(2)}
                        </h4>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Requested Date:</td>
                      <td>
                        <FaCalendar className="me-2" />
                        {new Date(withdrawal.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                    {withdrawal.processed_at && (
                      <tr>
                        <td className="text-muted">Processed Date:</td>
                        <td>
                          {new Date(withdrawal.processed_at).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    )}
                    {withdrawal.admin_remarks && (
                      <tr>
                        <td className="text-muted">Admin Remarks:</td>
                        <td>
                          <Alert variant={withdrawal.status === 'rejected' ? 'danger' : 'info'} className="mb-0">
                            {withdrawal.admin_remarks}
                          </Alert>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Bank Details */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaUniversity className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Bank Account Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: '40%' }}>Account Holder Name:</td>
                      <td className="fw-bold">{withdrawal.account_holder_name}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Bank Name:</td>
                      <td className="fw-bold">{withdrawal.bank_name}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Account Number:</td>
                      <td>
                        <code className="bg-light p-2 rounded">
                          {withdrawal.bank_account_number}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">IFSC Code:</td>
                      <td>
                        <code className="bg-light p-2 rounded">
                          {withdrawal.bank_ifsc}
                        </code>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <Alert variant="info" className="mb-0 small">
                  <strong>⚠️ Important:</strong> Please verify these bank details before processing the payment. 
                  Ensure the account holder name matches the user's registered name.
                </Alert>
              </Card.Body>
            </Card>
          </div>

          {/* User Information */}
          <div className="col-md-4">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaUser className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  User Information
                </h5>
              </Card.Header>
              <Card.Body>
                {withdrawal.user ? (
                  <>
                    <div className="text-center mb-3">
                      <div 
                        className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          backgroundColor: 'var(--accent-cyan)',
                          fontSize: '32px',
                          fontWeight: 'bold',
                          color: 'var(--bg-dark)'
                        }}
                      >
                        {withdrawal.user.username?.charAt(0).toUpperCase()}
                      </div>
                      <h5 className="fw-bold mb-1">{withdrawal.user.username}</h5>
                      <p className="text-muted mb-0 small">{withdrawal.user.email}</p>
                    </div>

                    <hr />

                    <Table borderless className="mb-0 small">
                      <tbody>
                        <tr>
                          <td className="text-muted">User ID:</td>
                          <td className="text-end fw-bold">#{withdrawal.user.id}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Phone:</td>
                          <td className="text-end">{withdrawal.user.phone || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Role:</td>
                          <td className="text-end">
                            <Badge bg="info">{withdrawal.user.user_role}</Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td className="text-end">
                            <Badge bg={withdrawal.user.status === 'approved' ? 'success' : 'warning'}>
                              {withdrawal.user.status}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Account:</td>
                          <td className="text-end">
                            <Badge bg={withdrawal.user.account_status === 'active' ? 'success' : 'secondary'}>
                              {withdrawal.user.account_status || 'inactive'}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Wallet Balance:</td>
                          <td className="text-end fw-bold">₹{withdrawal.user.wallet_balance}</td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="d-grid gap-2 mt-3">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => navigate(`/admin/users/${withdrawal.user.id}`)}
                      >
                        View User Profile
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted text-center mb-0">User information not available</p>
                )}
              </Card.Body>
            </Card>

            {/* Processing Instructions */}
            {withdrawal.status === 'pending' && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0 fw-bold">Processing Instructions</h6>
                </Card.Header>
                <Card.Body>
                  <ol className="mb-0 small ps-3">
                    <li className="mb-2">Verify user's KYC status is approved</li>
                    <li className="mb-2">Confirm bank account details match user's registered information</li>
                    <li className="mb-2">Process bank transfer using provided details</li>
                    <li className="mb-2">Mark as approved once payment is initiated</li>
                    <li className="mb-0">User will receive email notification</li>
                  </ol>
                </Card.Body>
              </Card>
            )}
          </div>
        </div>

        {/* Approve Modal */}
        <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Approve Withdrawal Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <div 
                className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: 'rgba(40, 167, 69, 0.1)'
                }}
              >
                <FaCheck size={40} style={{ color: '#28a745' }} />
              </div>
              <h5>Approve & Process Withdrawal?</h5>
              <p className="text-muted">Request ID: <strong>{withdrawal.request_id}</strong></p>
            </div>

            <Alert variant="success">
              <strong>Payment Details:</strong>
              <hr />
              <div className="mb-2"><strong>Amount:</strong> ₹{parseFloat(withdrawal.amount).toFixed(2)}</div>
              <div className="mb-2"><strong>Bank:</strong> {withdrawal.bank_name}</div>
              <div className="mb-2"><strong>Account:</strong> {withdrawal.bank_account_number}</div>
              <div className="mb-2"><strong>IFSC:</strong> {withdrawal.bank_ifsc}</div>
              <div><strong>Beneficiary:</strong> {withdrawal.account_holder_name}</div>
            </Alert>

            <Alert variant="warning" className="mb-0">
              <strong>⚠️ Confirmation:</strong> By approving, you confirm that:
              <ul className="mb-0 mt-2 ps-3">
                <li>Bank details have been verified</li>
                <li>Payment will be processed immediately</li>
                <li>This action cannot be undone</li>
              </ul>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowApproveModal(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={handleApproveWithdrawal}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FaCheck className="me-2" />
                  Approve & Process
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Reject Modal */}
        <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Reject Withdrawal Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <div 
                className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: 'rgba(220, 53, 69, 0.1)'
                }}
              >
                <FaTimes size={40} style={{ color: '#dc3545' }} />
              </div>
              <h5>Reject Withdrawal Request?</h5>
              <p className="text-muted">
                Amount: <strong>₹{parseFloat(withdrawal.amount).toFixed(2)}</strong>
              </p>
            </div>

            <Form>
              <Form.Group>
                <Form.Label>Rejection Reason *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectionRemarks}
                  onChange={(e) => setRejectionRemarks(e.target.value)}
                  placeholder="Please provide a clear reason for rejection..."
                  required
                />
                <Form.Text className="text-muted">
                  Minimum 10 characters required
                </Form.Text>
              </Form.Group>
            </Form>

            <Alert variant="info" className="mt-3 mb-0 small">
              <strong>Note:</strong> The withdrawal amount will be refunded to the user's wallet, 
              and they will receive an email with the rejection reason.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowRejectModal(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleRejectWithdrawal}
              disabled={processing || rejectionRemarks.length < 10}
            >
              {processing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Rejecting...
                </>
              ) : (
                <>
                  <FaTimes className="me-2" />
                  Reject Request
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default AdminWithdrawalDetails;